/**
 * Notificacoes — Pagina do feed unificado de notificacoes.
 *
 * Spec docs/notificacoes/IMPLEMENTATION_SPEC_V1V2.md §3.2.
 *
 * Orquestra:
 *  - NotificacoesFilters (status, sources, severity, q)
 *  - NewItemsBanner (SSE realtime — preview, TASK-2577)
 *  - DataCard com FeedItem rows
 *  - BulkActions no slot `extra` quando ha selecao
 *  - EmptyState diferenciando "tudo em dia" vs "sem resultados pra filtros"
 *
 * Sem <Title> (vem do ROUTE_MAP via PageHeader em AppLayout).
 */
import { Fragment, useState, useMemo } from 'react'
import { Stack, Alert, Group, Button, Text } from '@mantine/core'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { IconBellRinging, IconFilterOff, IconSettings, IconRefresh } from '@tabler/icons-react'
import { DataCard } from '@/components/ui/DataCard'
import { EmptyState } from '@/components/ui/EmptyState'
import { useNotificationsFeed } from '@/hooks/useNotificationsFeed'
import { useNotificationsRealtime } from '@/hooks/useNotificationsRealtime'
import type { FeedFilters, FeedStatus, Severity, FeedItem as FeedItemType } from '@/api/notifications-feed'
import { NotificacoesFilters } from './NotificacoesFilters'
import { FeedItem } from './FeedItem'
import { BulkActions } from './BulkActions'
import { NewItemsBanner } from './NewItemsBanner'

function parseFiltersFromURL(params: URLSearchParams): FeedFilters {
  return {
    status: (params.get('status') as FeedStatus) || 'unread',
    sources: params.get('sources')?.split(',').filter(Boolean) ?? [],
    severity: (params.get('severity')?.split(',').filter(Boolean) ?? []) as Severity[],
    q: params.get('q') ?? undefined,
    date_from: params.get('date_from') ?? undefined,
    date_to: params.get('date_to') ?? undefined,
  }
}

function serializeFilters(f: FeedFilters): URLSearchParams {
  const p = new URLSearchParams()
  if (f.status && f.status !== 'unread') p.set('status', f.status)
  if (f.sources && f.sources.length > 0) p.set('sources', f.sources.join(','))
  if (f.severity && f.severity.length > 0) p.set('severity', f.severity.join(','))
  if (f.q) p.set('q', f.q)
  if (f.date_from) p.set('date_from', f.date_from)
  if (f.date_to) p.set('date_to', f.date_to)
  return p
}

function toggleInSet(setFn: React.Dispatch<React.SetStateAction<Set<number>>>, id: number) {
  setFn((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
}

interface DayGroup {
  day: string
  dayLabel: string
  items: FeedItemType[]
}

function groupByDayInline(items: FeedItemType[]): DayGroup[] {
  const out: DayGroup[] = []
  let currentDay = ''
  let bucket: FeedItemType[] = []
  const today = new Date().toISOString().slice(0, 10)
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10)
  for (const it of items) {
    const day = (it.occurred_at || '').slice(0, 10)
    if (day !== currentDay) {
      if (bucket.length > 0) {
        out.push({ day: currentDay, dayLabel: dayLabelFor(currentDay, today, yesterday), items: bucket })
      }
      currentDay = day
      bucket = [it]
    } else {
      bucket.push(it)
    }
  }
  if (bucket.length > 0) {
    out.push({ day: currentDay, dayLabel: dayLabelFor(currentDay, today, yesterday), items: bucket })
  }
  return out
}

function dayLabelFor(day: string, today: string, yesterday: string): string {
  if (day === today) return 'Hoje'
  if (day === yesterday) return 'Ontem'
  try {
    return new Date(day).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })
  } catch {
    return day
  }
}

/**
 * Extrai `error_code` + `message` do erro retornado pelo axios/FastAPI.
 *
 * Backend retorna erros como:
 *  - { detail: { error_code, message } }  (FastAPI HTTPException structured)
 *  - { error_code, message }              (top-level)
 *  - network errors (sem response)
 *
 * Modulo-escopo (nao re-criar por render).
 */
function extractErrorDetail(err: unknown): { code: string; message: string } {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = (err as { response?: { data?: { error_code?: string; message?: string; detail?: { error_code?: string; message?: string } } } }).response
    const detail = resp?.data?.detail ?? resp?.data
    return {
      code: detail?.error_code ?? 'unknown_error',
      message: detail?.message ?? (err as unknown as Error).message ?? 'Erro desconhecido',
    }
  }
  return { code: 'network_error', message: (err as Error)?.message ?? 'Erro de rede' }
}

export default function Notificacoes() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const filters = useMemo(() => parseFiltersFromURL(params), [params])
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  const { data, fetchNextPage, hasNextPage, refetch, isLoading, isError, error } = useNotificationsFeed(filters)
  const { newItemsCount, resetCount } = useNotificationsRealtime({
    sources: filters.sources,
    severity: filters.severity,
  })

  const items = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  )
  const hasActiveFilters =
    (filters.sources && filters.sources.length > 0) ||
    (filters.severity && filters.severity.length > 0) ||
    !!filters.q ||
    !!filters.date_from ||
    !!filters.date_to

  return (
    <Stack gap="xl">
      <Alert color="blue" variant="light">
        Sino do header mostra apenas as 5 mais recentes. Aqui você vê todo o histórico, filtra
        por origem e severidade, e gerencia em massa. Configure quietude e canais em{' '}
        <Text
          component="a"
          c="blue"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/notificacoes/preferencias')}
        >
          Preferências
        </Text>
        .
      </Alert>

      <NotificacoesFilters
        value={filters}
        onChange={(f) => setParams(serializeFilters(f))}
      />

      {newItemsCount > 0 && (
        <NewItemsBanner
          count={newItemsCount}
          onShow={() => { refetch(); setExpanded(new Set()); resetCount() }}
        />
      )}

      <DataCard flush
        title="Histórico de notificações"
        accent="info"
        extra={
          selected.size > 0 ? (
            <BulkActions
              selected={selected}
              onClear={() => setSelected(new Set())}
              onDone={() => { setSelected(new Set()); refetch() }}
            />
          ) : (
            <Group gap="xs">
              <Button
                size="xs"
                variant="subtle"
                leftSection={<IconRefresh size={14} />}
                onClick={() => refetch()}
                disabled={isLoading}
              >
                Atualizar
              </Button>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconSettings size={14} />}
                onClick={() => navigate('/notificacoes/preferencias')}
              >
                Preferências
              </Button>
            </Group>
          )
        }
      >
        {isError ? (
          (() => {
            const errDetail = extractErrorDetail(error)
            return (
              <EmptyState
                bare
                size="md"
                accent="warning"
                icon={IconFilterOff}
                title="Falha ao carregar notificações"
                description={
                  <>
                    <Text size="sm">{errDetail.message}</Text>
                    <Text size="xs" c="dimmed" mt="xs" ff="monospace">
                      {errDetail.code}
                    </Text>
                  </>
                }
                primaryAction={{ label: 'Tentar novamente', onClick: () => refetch() }}
              />
            )
          })()
        ) : items.length === 0 ? (
          <EmptyState
            bare
            size="md"
            accent={hasActiveFilters ? 'warning' : 'primary'}
            icon={hasActiveFilters ? IconFilterOff : IconBellRinging}
            title={hasActiveFilters ? 'Nenhuma notificação com esses filtros' : 'Tudo em dia!'}
            description={
              hasActiveFilters
                ? 'Tente ampliar o intervalo de datas ou remover filtros.'
                : 'Nada pendente por aqui. Quando chegar uma notificação importante, ela aparece automaticamente.'
            }
            primaryAction={
              hasActiveFilters
                ? { label: 'Limpar filtros', onClick: () => setParams({}) }
                : { label: 'Configurar preferências', onClick: () => navigate('/notificacoes/preferencias') }
            }
          />
        ) : (
          <Stack gap={0}>
            {groupByDayInline(items).map((group) => (
              <Fragment key={group.day}>
                <Group
                  gap="xs"
                  px="md"
                  py="xs"
                  style={{ borderBottom: '1px solid var(--ai-border-light)' }}
                >
                  <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                    {group.dayLabel}
                  </Text>
                </Group>
                {group.items.map((item) => (
                  <FeedItem
                    key={item.event_id}
                    item={item}
                    isSelected={selected.has(item.event_id)}
                    onToggleSelect={(id) => toggleInSet(setSelected, id)}
                    isExpanded={expanded.has(item.event_id)}
                    onToggleExpand={(id) => toggleInSet(setExpanded, id)}
                  />
                ))}
              </Fragment>
            ))}
            {hasNextPage && (
              <Group justify="center" p="md">
                <Button variant="default" onClick={() => fetchNextPage()}>
                  Carregar mais
                </Button>
              </Group>
            )}
          </Stack>
        )}
      </DataCard>
    </Stack>
  )
}
